import * as THREE from 'three';
import type { SurfaceTextures } from './surfaceTextures';
import { TILE_SIZE } from '../gen/config';
import { mergeInteriorParts } from '../worker/interiorMerge';
import type { InteriorResult } from '../worker/protocol';
import { genToThree } from './coords';
import { FurnitureAssets } from './furnitureAssets';
import { type RoomSources, bakeMesh, irradiance, windowSamples } from '../gen/interiors/lighting';
import { type TileManager, disposeGroup, ignoreCancel, makeGeometry } from './tileManager';
import type { WorkerPool } from './workerPool';

export interface ActiveFloor {
  key: string;
  tx: number;
  ty: number;
  buildingId: string;
  floor: number;
  group: THREE.Group;
  result: InteriorResult;
}

export interface InteriorOptions {
  /** Start generating interiors when the camera is within this distance of a tower footprint (cm). */
  nearDistance: number;
  /** Discard interiors beyond this distance (cm). */
  farDistance: number;
  /** Floors above and below the camera's floor. */
  floorSpan: number;
  /** Max towers with interiors at once. */
  maxBuildings: number;
  /** Max row houses with interiors at once (all their floors). */
  maxHouses: number;
}

function distToPolygon(x: number, y: number, flat: number[]): number {
  let inside = false;
  let best = Infinity;
  const n = flat.length / 2;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const ax = flat[i * 2];
    const ay = flat[i * 2 + 1];
    const bx = flat[j * 2];
    const by = flat[j * 2 + 1];
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    let t = l2 > 0 ? ((x - ax) * dx + (y - ay) * dy) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    best = Math.min(best, Math.hypot(ax + dx * t - x, ay + dy * t - y));
  }
  return inside ? 0 : best;
}

/** A building whose interiors can be generated: a tower, or one row house of a low-rise. */
interface Candidate {
  id: string;
  baseZ: number;
  floors: number;
  floorHeight: number;
  baseFloorTop: number;
  /** Load every floor (row houses) rather than the camera's floor ± span. */
  whole: boolean;
}

const isFixture = (name: string) => name.startsWith('ceiling_') && !name.includes('fan');

/** Each room's light sources: its ceiling fixtures (just below the ceiling) and its window openings. */
function roomSources(r: InteriorResult): RoomSources[] {
  return r.rooms.map((room) => {
    const fixtures: [number, number, number][] = [];
    for (const o of room.objects) {
      if (o.kind !== 'ceiling' || !isFixture(o.name)) continue;
      let x = 0;
      let y = 0;
      const n = o.outline.length / 2;
      for (let k = 0; k < n; k++) {
        x += o.outline[k * 2];
        y += o.outline[k * 2 + 1];
      }
      fixtures.push([x / n, y / n, r.z0 + room.roof - 8]);
    }
    if (room.type === 'elevator') {
      // The core has no decorated fixtures: a light high in the stairwell and one at the half-landing.
      let x = 0;
      let y = 0;
      const n = room.poly.length / 2;
      for (let k = 0; k < n; k++) {
        x += room.poly[k * 2];
        y += room.poly[k * 2 + 1];
      }
      fixtures.push([x / n, y / n, r.z0 + room.roof - 30], [x / n, y / n, r.z0 + room.roof / 2 + 200]);
    }
    const windows: RoomSources['windows'] = [];
    for (let k = 0; k + 7 < room.windows.length; k += 8) {
      const w = room.windows;
      windows.push({ x1: w[k], y1: w[k + 1], x2: w[k + 2], y2: w[k + 3], z0: w[k + 4], z1: w[k + 5], nx: w[k + 6], ny: w[k + 7] });
    }
    return { fixtures, windows };
  });
}

/**
 * Interiors only for towers near the camera: the camera's floor ± span, generated in workers and
 * discarded at distance (like ASpawnBuilding's floorBelow/Current/Above, SPEC §4.7).
 */
export class InteriorManager {
  /** Get a new floor's shaders compiled before it's shown (set by the app: renderer.compileAsync). */
  prepare: ((g: THREE.Group) => Promise<unknown>) | null = null;
  readonly floors = new Map<string, ActiveFloor>();
  private readonly pending = new Set<string>();
  readonly root = new THREE.Group();
  /** Interiors are fully baked (lighting.ts): unlit, still tone mapped. */
  private readonly material = new THREE.MeshBasicMaterial({ vertexColors: true });
  private readonly furniture = new FurnitureAssets();
  private last = 0;
  selected: { floorKey: string; room: number } | null = null;

  constructor(
    private readonly pool: WorkerPool,
    private readonly tiles: TileManager,
    private seed: number,
    surfaces: SurfaceTextures | null,
    readonly opts: InteriorOptions,
  ) {
    this.root.name = 'interiors';
    surfaces?.patch(this.material, { basic: true });
  }

  get pendingCount(): number {
    return this.pending.size;
  }

  setSeed(seed: number): void {
    this.seed = seed;
    this.clear();
  }

  clear(): void {
    this.pool.cancelWhere((k) => k.startsWith('i:'));
    for (const f of this.floors.values()) this.remove(f);
    this.floors.clear();
    this.pending.clear();
    this.selected = null;
    this.updateCutouts();
  }

  update(cx: number, cy: number, cz: number, now: number): void {
    if (now - this.last < 250) return;
    this.last = now;
    // Towers (the camera's floor ± span) and row houses (every floor: they're small).
    const towers: { tx: number; ty: number; b: Candidate; d: number }[] = [];
    const houses: { tx: number; ty: number; b: Candidate; d: number }[] = [];
    for (const t of this.tiles.tiles.values()) {
      if (!t.buildings || t.cityLevel !== 2) continue;
      for (const b of t.buildings) {
        if (b.kind === 'tower') {
          const d = distToPolygon(cx, cy, b.footprint);
          if (d < this.opts.farDistance) towers.push({ tx: t.tx, ty: t.ty, b: { id: b.id, baseZ: b.baseZ, floors: b.floors, floorHeight: b.floorHeight, baseFloorTop: b.baseFloorTop, whole: false }, d });
        } else if (b.kind === 'lowrise') {
          for (const u of b.units ?? []) {
            const d = distToPolygon(cx, cy, u.poly);
            if (d < this.opts.farDistance) houses.push({ tx: t.tx, ty: t.ty, b: { id: u.id, baseZ: u.baseZ, floors: u.floors, floorHeight: b.floorHeight, baseFloorTop: 0, whole: true }, d });
          }
        }
      }
    }
    towers.sort((a, b) => a.d - b.d);
    houses.sort((a, b) => a.d - b.d);
    const desired = new Map<string, { tx: number; ty: number; b: Candidate; floor: number; d: number }>();
    const keepRange = new Map<string, [number, number]>();
    [...towers.slice(0, this.opts.maxBuildings), ...houses.slice(0, this.opts.maxHouses)].forEach((c) => {
      const b = c.b;
      const f0 = Math.max(b.baseFloorTop, Math.min(b.floors - 1, Math.floor((cz - (b.baseZ - 2)) / b.floorHeight)));
      const [lo, hi] = b.whole ? [0, b.floors - 1] : [f0 - this.opts.floorSpan, f0 + this.opts.floorSpan];
      keepRange.set(b.id, b.whole ? [lo, hi] : [lo - 1, hi + 1]);
      if (c.d > this.opts.nearDistance) return;
      for (let f = lo; f <= hi; f++) {
        if (f < b.baseFloorTop || f >= b.floors) continue;
        desired.set(`i:${b.id}:${f}`, { tx: c.tx, ty: c.ty, b, floor: f, d: c.d });
      }
    });
    for (const [key, want] of desired) {
      if (this.floors.has(key) || this.pending.has(key)) continue;
      this.pending.add(key);
      const prio = 0.2 + want.d / TILE_SIZE + Math.abs(want.floor - Math.floor((cz - want.b.baseZ) / want.b.floorHeight)) * 0.01;
      // Rooms are decorated in parallel: one part per worker, merged in room order.
      const n = this.pool.size;
      const parts = Array.from({ length: n }, (_, j) =>
        this.pool.submit('interior', this.seed, { tx: want.tx, ty: want.ty, buildingId: want.b.id, floor: want.floor, part: [j, n] }, prio, `${key}#${j}`),
      );
      void Promise.all(parts)
        .then(mergeInteriorParts)
        .then((r) =>
          this.tiles.enqueue(prio, () => {
            this.pending.delete(key);
            this.add(key, r);
          }),
        )
        .catch((e) => {
          this.pending.delete(key);
          ignoreCancel(e);
        });
    }
    for (const key of [...this.pending]) {
      if (!desired.has(key)) {
        for (let j = 0; j < this.pool.size; j++) this.pool.cancel(`${key}#${j}`);
        this.pending.delete(key);
      }
    }
    for (const [key, f] of this.floors) {
      const range = keepRange.get(f.buildingId);
      if (!range || f.floor < range[0] || f.floor > range[1]) {
        this.remove(f);
        this.floors.delete(key);
        if (this.selected?.floorKey === key) this.selected = null;
      }
    }
    this.updateCutouts();
  }

  /**
   * Open the facade glass of each tower over its longest contiguous run of loaded interior floors,
   * so the interiors show from outside (the run's shell stays opaque elsewhere).
   */
  updateCutouts(): void {
    const byBuilding = new Map<string, ActiveFloor[]>();
    for (const f of this.floors.values()) {
      // Floors still compiling their shaders aren't shown yet: no hole in the glass for them.
      if (f.group.parent !== this.root) continue;
      const l = byBuilding.get(f.buildingId);
      if (l) l.push(f);
      else byBuilding.set(f.buildingId, [f]);
    }
    const perTile = new Map<string, Map<string, [number, number]>>();
    for (const t of this.tiles.tiles.values()) if (t.city) perTile.set(t.key, new Map());
    for (const [id, fl] of byBuilding) {
      const t = this.tiles.tiles.get(`${fl[0].tx},${fl[0].ty}`);
      // Towers by id; a row house's floor height is its low-rise's.
      const info = t?.buildings?.find((b) => b.id === id || b.units?.some((u) => u.id === id));
      const cuts = t && perTile.get(t.key);
      if (!info || !cuts) continue;
      fl.sort((a, b) => a.floor - b.floor);
      let best: [number, number] = [0, 0];
      for (let i = 0, j = 0; i < fl.length; i = j) {
        for (j = i + 1; j < fl.length && fl[j].floor === fl[j - 1].floor + 1; ) j++;
        if (j - i > best[1] - best[0]) best = [i, j];
      }
      const lo = fl[best[0]].result.z0;
      const hi = fl[best[1] - 1].result.z0 + info.floorHeight;
      cuts.set(id, [lo / 100, hi / 100]);
    }
    for (const [key, cuts] of perTile) this.tiles.setCutouts(this.tiles.tiles.get(key)!, cuts);
  }

  private add(key: string, r: InteriorResult): void {
    if (this.floors.has(key)) return;
    const g = new THREE.Group();
    g.name = key;
    genToThree(r.tx * TILE_SIZE, r.ty * TILE_SIZE, 0, g.position);
    // Baked lighting: each room lit by its own ceiling fixtures and windows (lighting.ts).
    const ox = r.tx * TILE_SIZE;
    const oy = r.ty * TILE_SIZE;
    const sources = roomSources(r);
    if (r.mesh.indices.length > 0 && r.mesh.colors) {
      const ranges = r.rooms.map((room) => room.vertexRange ?? ([0, 0] as [number, number]));
      const baked = bakeMesh(r.mesh.positions, r.mesh.normals, r.mesh.colors, ranges, sources, { ox, oy });
      g.add(new THREE.Mesh(makeGeometry({ ...r.mesh, colors: baked }), this.material));
    }
    // Light at each piece of furniture: its room's irradiance at the object (lamps glow).
    const light = new Float32Array(r.furniture.kinds.length * 3);
    let at = 0;
    const e: [number, number, number] = [0, 0, 0];
    const names: string[] = [];
    r.rooms.forEach((room, ri) => {
      const samples = windowSamples(sources[ri]);
      for (const o of room.objects) {
        const m = r.furniture.matrices;
        const gx = m[at * 16 + 12] * 100 + ox;
        const gy = -m[at * 16 + 14] * 100 + oy;
        const gz = m[at * 16 + 13] * 100;
        if (isFixture(o.name)) e[0] = e[1] = e[2] = 3;
        else irradiance(sources[ri], samples, gx, gy, gz + 60, 0, 0, 1, e);
        light.set(e, at * 3);
        names.push(o.name);
        at++;
      }
    });
    // All the floor's furniture in one batched mesh: each catalogue model once, every piece an instance
    // (one multi-draw call per floor; one instanced mesh per model cost hundreds of draws).
    const count = r.furniture.kinds.length;
    if (count > 0) {
      const kinds = [...new Set(r.furniture.kinds)];
      const verts = kinds.reduce((n, k) => n + this.furniture.geometry(k).getAttribute('position').count, 0);
      const bm = new THREE.BatchedMesh(count, verts, 0, this.furniture.material);
      const gid = new Map(kinds.map((k) => [k, bm.addGeometry(this.furniture.geometry(k))]));
      const mat = new THREE.Matrix4();
      const col = new THREE.Color();
      const fixture: boolean[] = [];
      for (let i = 0; i < count; i++) {
        const id = bm.addInstance(gid.get(r.furniture.kinds[i])!);
        bm.setMatrixAt(id, mat.fromArray(r.furniture.matrices, i * 16));
        bm.setColorAt(id, col.fromArray(light, i * 3));
        fixture[id] = names[i].startsWith('ceiling_');
      }
      bm.name = 'furniture';
      // Ceiling fixtures are out of reach: no colliders for them (navigation.ts).
      bm.userData.fixture = fixture;
      bm.computeBoundingSphere();
      bm.computeBoundingBox();
      g.add(bm);
    }
    this.floors.set(key, { key, tx: r.tx, ty: r.ty, buildingId: r.buildingId, floor: r.floor, group: g, result: r });
    // Compile the floor's shaders off the frame (parallel compile) before it appears; after the first
    // floors the programs exist and this resolves at once.
    const show = () => {
      if (this.floors.get(key)?.group !== g) return;
      this.root.add(g);
      this.updateCutouts();
    };
    if (this.prepare) void this.prepare(g).then(show, show);
    else show();
  }

  private remove(f: ActiveFloor): void {
    disposeGroup(f.group);
    this.root.remove(f.group);
  }

  /** Select the room under a generation-space point, preferring the floor closest to `z`. */
  pick(x: number, y: number, z: number): boolean {
    let best: { floorKey: string; room: number; dz: number } | null = null;
    for (const f of this.floors.values()) {
      f.result.rooms.forEach((r, i) => {
        if (distToPolygon(x, y, r.poly) > 0) return;
        const dz = Math.abs(f.result.z0 - z);
        if (!best || dz < best.dz) best = { floorKey: f.key, room: i, dz };
      });
    }
    const b = best as { floorKey: string; room: number; dz: number } | null;
    this.selected = b ? { floorKey: b.floorKey, room: b.room } : null;
    return !!b;
  }
}
